import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, Send } from 'lucide-react';
import type { ScheduleTargets } from '../lib/scheduleTargets';

interface ScheduleSocialPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * targetPageId: pagina-id, null (LinkedIn persoonlijk) of undefined
   * (geen doelsectie van toepassing; aanroeper schrijft dan geen doelkolom).
   */
  onConfirm: (scheduledAt: Date, targetPageId?: string | null) => void;
  platform: string;
  /** true zolang de paginacheck loopt (bevestig-knop disabled). */
  checking?: boolean;
  /** Gevuld = inplannen geblokkeerd; melding vervangt het keuzeformulier. */
  blockReason?: string | null;
  onGoToSettings?: () => void;
  /** Doelen voor de "Plaatsen op"-sectie; null/undefined = n.v.t. of nog ladend. */
  targets?: ScheduleTargets | null;
  /** Doel-snapshot van de post zelf (facebook_page_id/linkedin_page_id, evt. 'personal'). */
  postTargetId?: string | null;
}

export function ScheduleSocialPostModal({
  isOpen,
  onClose,
  onConfirm,
  platform,
  checking,
  blockReason,
  onGoToSettings,
  targets,
  postTargetId,
}: ScheduleSocialPostModalProps) {
  const [scheduleOption, setScheduleOption] = useState<'now' | 'later'>('now');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [error, setError] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const minDate = new Date(now.getTime() + 6 * 60000);

      setSelectedDate(minDate.toISOString().split('T')[0]);
      setSelectedTime(minDate.toTimeString().slice(0, 5));
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!targets) {
      setSelectedTarget('');
      return;
    }
    // Voorselectie: post-snapshot wint ('personal' → lege waarde =
    // Persoonlijk profiel), anders profiel-default, anders leeg.
    if (postTargetId === 'personal') {
      setSelectedTarget('');
    } else if (postTargetId) {
      setSelectedTarget(postTargetId);
    } else {
      setSelectedTarget(targets.defaultPageId ?? '');
    }
  }, [isOpen, targets, postTargetId]);

  if (!isOpen) return null;

  const getScheduledDateTime = (): Date | null => {
    if (scheduleOption === 'now') {
      return new Date(Date.now() + 5 * 60000);
    }

    if (!selectedDate || !selectedTime) {
      setError('Selecteer een datum en tijd');
      return null;
    }

    const scheduled = new Date(`${selectedDate}T${selectedTime}`);
    const now = new Date();
    const minTime = new Date(now.getTime() + 5 * 60000);

    if (scheduled < minTime) {
      setError('Geplande tijd moet minimaal 5 minuten in de toekomst zijn');
      return null;
    }

    return scheduled;
  };

  const handleConfirm = () => {
    const scheduledAt = getScheduledDateTime();
    if (!scheduledAt) return;
    const targetPageId = showTargetSection
      ? selectedTarget === ''
        ? null
        : selectedTarget
      : undefined;
    onConfirm(scheduledAt, targetPageId);
    onClose();
  };

  const formatScheduledTime = () => {
    const dt = getScheduledDateTime();
    if (!dt) return '';

    return dt.toLocaleString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);

  const showTargetSection = !!targets && !blockReason && (
    targets.personalOption
      ? targets.pages.length >= 1
      : targets.pages.length > 1
  );
  const needsTargetChoice =
    showTargetSection &&
    !!targets &&
    !targets.personalOption &&
    selectedTarget === '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Post Inplannen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {blockReason ? (
          <div className="px-6 py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-900">{blockReason}</p>
            </div>
            {onGoToSettings && (
              <button
                onClick={onGoToSettings}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Naar instellingen
              </button>
            )}
          </div>
        ) : (
          <div className="px-6 py-4 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                Je staat op het punt deze post te publiceren naar{' '}
                <span className="font-bold">{platformName}</span>
              </p>
            </div>

            {showTargetSection && targets && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Plaatsen op
                </label>
                <select
                  value={selectedTarget}
                  onChange={(e) => setSelectedTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {targets.personalOption ? (
                    <option value="">Persoonlijk profiel</option>
                  ) : (
                    selectedTarget === '' && (
                      <option value="" disabled>
                        Kies een pagina...
                      </option>
                    )
                  )}
                  {targets.pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Wanneer wil je publiceren?
              </label>

              <button
                onClick={() => setScheduleOption('now')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  scheduleOption === 'now'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  scheduleOption === 'now' ? 'border-blue-600' : 'border-gray-300'
                }`}>
                  {scheduleOption === 'now' && (
                    <div className="w-3 h-3 rounded-full bg-blue-600" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Send className="w-4 h-4 text-gray-700" />
                    <span className="font-semibold text-gray-900">Nu Versturen</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Post wordt ingepland voor over 5 minuten
                  </p>
                </div>
              </button>

              <button
                onClick={() => setScheduleOption('later')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  scheduleOption === 'later'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  scheduleOption === 'later' ? 'border-blue-600' : 'border-gray-300'
                }`}>
                  {scheduleOption === 'later' && (
                    <div className="w-3 h-3 rounded-full bg-blue-600" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-gray-700" />
                    <span className="font-semibold text-gray-900">Inplannen voor Later</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Kies een specifieke datum en tijd
                  </p>
                </div>
              </button>
            </div>

            {scheduleOption === 'later' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Datum
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setError('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Tijd
                  </label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => {
                      setSelectedTime(e.target.value);
                      setError('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {!error && (scheduleOption === 'now' || (selectedDate && selectedTime)) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-900">
                  <span className="font-semibold">Ingepland voor:</span>
                  <br />
                  {formatScheduledTime()}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Annuleren
          </button>
          {!blockReason && (
            <button
              onClick={handleConfirm}
              disabled={checking || needsTargetChoice}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {checking
                ? 'Even controleren...'
                : needsTargetChoice
                  ? 'Kies eerst een pagina'
                  : 'Bevestig en Plan In'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
